class ReconnectingWebSocket {
    /**
     * @param {string} url
     * @param {Object} options
     * @param {string} options.token
     * @param {boolean} options.reconnectEnabled true
     * @param {number} options.reconnectInterval 1000
     * @param {number} options.maxReconnectAttempts 5
     * @param {number} options.heartbeatTimeout 30000
     */
    constructor(url, options = {}) {
        this.url = url;
        this.options = {
            reconnectEnabled: true,
            reconnectInterval: 1000,
            maxReconnectAttempts: 5,
            heartbeatTimeout: 30000,
            ...options
        };
        this.socket = null;
        this.isConnected = false;
        this.reconnectAttempts = 0;
        this.heartbeatTimer = null;
        this.listeners = {};
        this.lastHeartbeatTime = 0;
        this.connect();
    }

    connect() {
        this.socket = new WebSocket(this.url + '?token=' + this.options.token);

        this.socket.onopen = () => {
            this.isConnected = true;
            this.reconnectAttempts = 0;
            this.emit('open');
        };

        this.socket.onmessage = (event) => {
            const message = JSON.parse(event.data);
            if (message.event === 'heartbeat') {
                this.handleHeartbeat(message.data);
            } else {
                this.emit('message', message);
            }
        };

        this.socket.onclose = () => {
            this.isConnected = false;
            this.stopHeartbeatCheck();
            this.emit('close');
            if (this.options.reconnectEnabled) {
                this.scheduleReconnect();
            }
        };

        this.socket.onerror = (error) => {
            this.emit('error', error);
        };
    }

    isConnected() {
        return this.isConnected;
    }

    scheduleReconnect() {
        if (this.reconnectAttempts < this.options.maxReconnectAttempts) {
            const delay = this.options.reconnectInterval * Math.pow(2, this.reconnectAttempts);
            setTimeout(() => {
                this.reconnectAttempts++;

                console.log(`socket config: ${JSON.stringify(this.options)}\n status: ${JSON.stringify({isConnected: this.isConnected,
                    reconnectAttempts: this.reconnectAttempts,
                    heartbeatTimer: this.heartbeatTimer,
                    lastHeartbeatTime: this.lastHeartbeatTime,})}`)

                this.connect();

            }, delay);
        } else {
            this.emit('reconnectFailed');
        }
    }

    handleHeartbeat(data) {
        this.lastHeartbeatTime = data.time;
        this.heartbeatInterval = data.interval * 1000;
        this.startHeartbeatCheck();
    }

    startHeartbeatCheck() {
        this.stopHeartbeatCheck(); // Clear any existing timer
        this.heartbeatTimer = setTimeout(() => {
            if (this.isConnected) {
                console.log('Heartbeat timeout, closing connection');
                this.socket.close();
            }
        }, this.heartbeatInterval);
    }

    stopHeartbeatCheck() {
        if (this.heartbeatTimer) {
            clearTimeout(this.heartbeatTimer);
            this.heartbeatTimer = null;
        }
    }

    send(data) {
        if (this.isConnected) {
            this.socket.send(JSON.stringify(data));
        } else {
            this.emit('error', new Error('WebSocket is not connected'));
        }
    }

    on(event, callback) {
        if (!this.listeners[event]) {
            this.listeners[event] = [];
        }
        this.listeners[event].push(callback);
    }

    off(event, callback = null) {
        if (callback === null) {
            this.listeners[event] = [];
            return;
        }
        if (this.listeners[event]) {
            this.listeners[event] = this.listeners[event].filter(cb => cb !== callback);
        }
    }

    emit(event, data) {
        if (this.listeners[event]) {
            this.listeners[event].forEach(callback => callback(data));
        }
    }

    close(reconnectEnabled = false) {
        this.options.reconnectEnabled = reconnectEnabled;
        this.stopHeartbeatCheck();
        if (this.socket) {
            this.socket.close();
        }
    }
}


export default ReconnectingWebSocket;
